/**
 * What the form builder should open with.
 *
 * Extracted from GenerateTemplatePopup because this one decision has now caused
 * the same data loss three times, always the same way: something concluded "this
 * form has no fields" from an answer that had not arrived yet, seeded a blank
 * template, and one Save later the real placements were gone — `form_markup` is
 * overwritten wholesale with no server-side history.
 *
 * The rule the callers kept getting wrong: **a blank slate is a claim, not a
 * default.** Seeding one asserts the server says this form has nothing saved.
 * Until the read that could contradict that has actually landed, the honest
 * answer is `null` — open nothing — not "open it empty".
 */

/** Pages keyed '0', '1', … each holding its placed fields. */
export type Template = Record<string, any>;

export interface TemplateInputs {
  /** Validated unsaved work-in-progress, or null. Already known to hold fields. */
  stashed?: Template | null;
  /** `selectedForm.formMarkup` — the Redux copy; may carry unsaved edits. */
  rowMarkup?: Template | null;
  /** The server's `form_markup` from the detail read. */
  serverMarkup?: Template | null;
  /** Whether that detail read has actually resolved. */
  markupLoaded?: boolean;
  /** Page-image count, used only when seeding a genuinely empty form. */
  pageCount?: number;
}

export interface TemplateResolution {
  template: Template;
  /** True only for restored work-in-progress — it is unsaved, so Save must be live. */
  enableSave: boolean;
  /** Which branch produced it. For tests and debugging. */
  source: 'stash' | 'saved' | 'seed';
}

export const seedPages = (pageCount: number): Template => {
  const seeded: Template = {};
  for (let i = 0; i < Math.max(0, pageCount || 0); i += 1) {
    seeded[`${i}`] = {};
  }
  return seeded;
};

/**
 * Resolve the builder's opening template, or `null` to leave it untouched.
 *
 * Precedence:
 *   1. `stashed`     — unsaved work beats everything, that is the point of it.
 *   2. `rowMarkup`   — then `serverMarkup`. The Redux copy leads because
 *                      EditedLabel writes edits through it; the server copy is
 *                      the fallback that survives a reopen, where the detail
 *                      response is cached and the Redux copy is not yet the
 *                      authority.
 *   3. seed          — only once `markupLoaded` proves there is nothing to lose.
 */
export const resolveInitialTemplate = ({
  stashed,
  rowMarkup,
  serverMarkup,
  markupLoaded,
  pageCount,
}: TemplateInputs): TemplateResolution | null => {
  if (stashed) {
    return { template: stashed, enableSave: true, source: 'stash' };
  }

  const saved = rowMarkup || serverMarkup;
  if (saved) {
    return { template: saved, enableSave: false, source: 'saved' };
  }

  if (markupLoaded) {
    return { template: seedPages(pageCount), enableSave: false, source: 'seed' };
  }

  // The read that decides between "empty form" and "not loaded yet" is still in
  // flight. Opening blank here is what destroyed the placements.
  return null;
};
